import { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  FileText,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import type { ConfirmationPayload, ConnectorSummary } from '@/types/api';
import { cn } from '@/lib/utils';

interface PublishConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly payload: ConfirmationPayload | null;
  readonly onConfirm: () => void;
  readonly isLoading: boolean;
}

const CONNECTOR_STATUS_ICON = {
  ready: { icon: CheckCircle2, className: 'text-green-400' },
  degraded: { icon: AlertTriangle, className: 'text-amber-400' },
  error: { icon: XCircle, className: 'text-red-400' },
} as const;

function ConnectorStatusRow({ connector }: { readonly connector: ConnectorSummary }) {
  const cfg = CONNECTOR_STATUS_ICON[connector.status];
  const StatusIcon = cfg.icon;

  return (
    <div className="flex items-center justify-between rounded-md bg-muted/30 px-3 py-2.5">
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium capitalize text-foreground">
            {connector.connectorType}
          </p>
          <p className="text-xs text-muted-foreground">
            {connector.documentCount} document{connector.documentCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusIcon className={cn('h-4 w-4', cfg.className)} />
        {connector.message && (
          <p className={cn('text-xs', cfg.className)}>{connector.message}</p>
        )}
      </div>
    </div>
  );
}

export function PublishConfirmDialog({
  open,
  onOpenChange,
  payload,
  onConfirm,
  isLoading,
}: PublishConfirmDialogProps) {
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  if (!payload) return null;

  const hasDependencyWarnings = payload.dependencyWarnings.length > 0;
  const hasConnectorErrors = payload.connectorSummary.some((c) => c.status === 'error');
  const canConfirm = !isLoading && (!hasDependencyWarnings || warningsAcknowledged);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setWarningsAcknowledged(false);
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirm Publish Batch</DialogTitle>
          <DialogDescription>
            Review the following before publishing{' '}
            {payload.totalDocuments} document{payload.totalDocuments !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto py-2">
          {/* Section 1: Connector Status */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              Publishing Targets
            </h3>
            <div className="space-y-2">
              {payload.connectorSummary.map((c) => (
                <ConnectorStatusRow key={c.connectorType} connector={c} />
              ))}
            </div>
            {hasConnectorErrors && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <p className="text-sm text-amber-300">
                  One or more connectors have errors. Publishing may partially fail.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Section 2: Dependency Warnings */}
          {hasDependencyWarnings && (
            <>
              <div>
                <h3 className="mb-3 text-sm font-semibold text-foreground">
                  Dependency Warnings
                </h3>
                <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                  {payload.dependencyWarnings.map((w) => (
                    <p key={`${w.cardId}-${w.dependsOnCardId}`} className="text-sm text-amber-300">
                      <span className="font-medium">{w.documentTitle}</span> depends on{' '}
                      <span className="font-medium">{w.dependsOnTitle}</span> which is not yet published.
                    </p>
                  ))}
                </div>
                <label className="mt-3 flex items-center gap-2.5">
                  <Checkbox
                    checked={warningsAcknowledged}
                    onCheckedChange={(checked) =>
                      setWarningsAcknowledged(checked === true)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    I acknowledge these dependency warnings and wish to proceed.
                  </span>
                </label>
              </div>
              <Separator />
            </>
          )}

          {/* Section 3: Summary */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">
              Summary
            </h3>
            <p className="text-sm text-foreground">
              {payload.totalDocuments} document{payload.totalDocuments !== 1 ? 's' : ''} will be published.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Estimated duration: {payload.estimatedDuration}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Publish Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
